import type { FirebaseConfig } from '@app/config'

import { reportSpawnFailure } from '@app/utils/errorReport/reportSpawnFailure'
import { createLogger } from '@app/utils/loggerCreator'

import { dispose } from '@app/packages/disposable/disposable'
import { spawn } from '@app/packages/disposable/spawn'

type FirebaseManagerUser = { id: string; account_type: 'parent' | 'sitter' }

export class FirebaseManager {
  private disposable: Disposable | null = null
  private connectPromise = Promise.withResolvers<void>()
  readonly user: FirebaseManagerUser

  constructor(user: FirebaseManagerUser, config: FirebaseConfig) {
    this.user = user

    this.disposable = spawn(async stack => {
      const ab = stack.adopt(new AbortController(), ab => ab.abort())

      try {
        this.log.log('Initializing...')
        stack.defer(() => this.log.log('Disposed'))
        const firebase = await import('firebase/app')
        ab.signal.throwIfAborted()

        firebase.initializeApp(config)
        this.log.log('Initialized')
        this.connectPromise.resolve()
      } catch (err) {
        this.connectPromise.reject(err)
        throw err
      }
    }, reportSpawnFailure('FirebaseManager'))
  }

  [Symbol.dispose]() {
    if (this.disposable) {
      dispose(this.disposable)
    }
  }

  connect() {
    return this.connectPromise.promise
  }

  private log = createLogger('Firebase', 'orange', 'bold')
}
